import { Form, Formik } from "formik";
import { atom, useAtom } from "jotai";
import { useNavigate, useParams } from "react-router-dom";
import { medicalVisitAtom } from "./AddCertificate";
import { formDataAtom } from "../App";
import * as Yup from "yup";
import { useEffect, useRef, useState } from "react";
import useFocusOnLoad from "../hooks/useFocusOnLoad";

import {
  Box,
  ErrorList,
  Button,
  handleSubmitClick,
  Progressbar,
  RadioGroupForm,
  Heading,
} from "@ogt/ui";

const ADD_MORE = "addMore";
const NO_MORE = "noMore";

type AddMoreFormValues = {
  addMore: string;
};

export const useIsAddMoreTrue = () => useAtom(isAddMoreTrueAtom);
export const isAddMoreTrueAtom = atom(false);
export default function AddMore() {
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const progress = 87;

  const [, setIsAddMoreTrue] = useIsAddMoreTrue();

  const { id } = useParams();
  const index = id === undefined ? 0 : Number.parseInt(id) - 1;
  const [mainFormData, setMainFormData] = useAtom(formDataAtom);
  const [formData, setFormData] = useAtom(medicalVisitAtom);
  const navigate = useNavigate();
  const isNewTravel = !mainFormData.medicalVisits[index];
  const formRef = useRef<HTMLFormElement>(null);
  const [shouldResetForm, setShouldResetForm] = useState(false);

  useEffect(() => {
    if (!isNewTravel && !shouldResetForm && isFormDataEmpty()) {
      setFormData(mainFormData.medicalVisits[index]);
    }
  }, [
    id,
    isNewTravel,
    mainFormData.medicalVisits,
    formData,
    setFormData,
    index,
    shouldResetForm,
  ]);

  const isFormDataEmpty = () => {
    return (
      !formData.certificate.length &&
      !formData.wayOfTravel &&
      !formData.receipts.length &&
      !formData.travelDescription &&
      !formData.comments &&
      !formData.dateTo &&
      !formData.dateFrom &&
      !formData.otherFiles.length
    );
  };

  useEffect(() => {
    if (shouldResetForm) {
      setFormData({
        certificate: [],
        wayOfTravel: "",
        receipts: [],
        travelDescription: "",
        comments: "",
        dateTo: "",
        dateFrom: "",
        otherFiles: [],
      });
      setShouldResetForm(false);
    }
  }, [shouldResetForm, setFormData]);
  return (
    <div>
      <Formik<AddMoreFormValues>
        onSubmit={(values) => {
          if (isNewTravel) {
            setMainFormData({
              ...mainFormData,
              medicalVisits: [...mainFormData.medicalVisits, formData],
            });
          } else {
            mainFormData.medicalVisits[index] = formData;
            setMainFormData({
              ...mainFormData,
              medicalVisits: [...mainFormData.medicalVisits],
            });
          }

          if (values.addMore === ADD_MORE) {
            setIsAddMoreTrue(true);
            setShouldResetForm(true);

            let offset = isNewTravel ? 2 : 1;
            navigate(
              "/reseuppgifter/" +
                (mainFormData.medicalVisits.length + offset) +
                "/intyg"
            );
          } else if (values.addMore === NO_MORE) {
            setIsAddMoreTrue(false);
            navigate("/granska-och-skicka");
          }
        }}
        initialValues={{
          addMore: isNewTravel ? "" : NO_MORE,
        }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object().shape({
          addMore: Yup.string().test(
            "at-least-one-radio-selected",
            "Vänligen välj ett svaralternativ för att gå vidare till nästa fråga",
            (value) => {
              return value === ADD_MORE || value === NO_MORE;
            }
          ),
        })}
      >
        {({ errors, values }) => (
          <>
            <div className="flex justify-end items-center">
              <div className="w-40 w-full max-w-xs mb-4">
                <Progressbar percentage={progress} />
              </div>
            </div>
            <Form ref={formRef} noValidate>
              <Box>
                <Heading
                  progress={progress}
                  headingRef={h2Ref}
                  focusable
                  id="AddMoreHeading"
                >
                  Vill du lägga till fler resor för denna patient?
                </Heading>

                <ErrorList errors={errors} />

                <RadioGroupForm
                  name="addMore"
                  ariaLabelledBy="AddMoreHeading"
                  orientation="vertical"
                  variant="box"
                  options={[
                    {
                      id: "addMoreT",
                      value: ADD_MORE,
                      label: "Ja",
                    },
                    {
                      id: "noMoreT",
                      value: NO_MORE,
                      label: "Nej, jag är klar och redo att skicka ansökan",
                    },
                  ]}
                />
              </Box>

              <div className="flex justify-center">
                <div className="w-full text-left flex items-center">
                  <Button
                    iconPosition="left"
                    iconSize={16}
                    iconName="arrow-left"
                    aria-label="Tillbaka"
                    showIcon
                    size="medium"
                    variant="outline"
                    onClick={(event) => {
                      event.preventDefault();
                      const backUrl = undefined;
                      if (backUrl) {
                        navigate(backUrl, { replace: true });
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Tillbaka
                  </Button>
                </div>
                <div className="w-full text-right">
                  <Button
                    onClick={handleSubmitClick(formRef)}
                    className="btn secondary"
                    type="submit"
                  >
                    Gå vidare
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}
